import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import apiService from '../../services/api';
import breederService from '../../services/breeder';
import OwedToUsCard from '../../components/cultivar/OwedToUsCard';
import type { ApiResponse } from '../../types/api';
import type { ReleaseState } from '../../types/breeder';
import { Award, Loader2, Users, TreePine, Leaf, Globe2, AlertCircle, CheckCircle, X, Settings, DollarSign } from 'lucide-react';

interface DashboardCultivar {
  id: number;
  name: string;
  trade_name: string | null;
  crop_type_name: string | null;
  release_state: ReleaseState | null;
  legacy_visibility_grace: boolean;
  primary_display_synonym: string | null;
  sublicenses_active: number | string;
  sublicenses_total: number | string;
  plantings_count: number | string;
  tree_count_total: number | string;
  hectares_total: number | string;
  earliest_planting: string | null;
  latest_planting: string | null;
  regions_with_licensor: number | string;
  regions_released_to_growers: number | string;
  per_tree_royalty: number | string;
  actual_production_royalty: number | string;
  default_production_royalty: number | string;
  annual_royalty_actual: number | string;
  annual_royalty_default: number | string;
  annual_royalty_estimate: number | string;
  latest_production_season: number | null;
  royalty_currency: string | null;
}

interface PendingClaim {
  id: number;
  region_code: string;
  scope: string;
  claimed_at: string;
  notes: string | null;
  licensor_org_name: string;
}

interface DashboardData {
  cultivars: DashboardCultivar[];
  totals: {
    cultivars: number;
    sublicenses_active: number;
    plantings: number;
    trees: number;
    hectares: number;
    per_tree_royalty: number;
    actual_production_royalty: number;
    default_production_royalty: number;
    annual_royalty_actual: number;
    annual_royalty_default: number;
    annual_royalty_estimate: number;
  };
  royalty_currency: string | null;
  royalty_currency_mixed: boolean;
  pending_claims: PendingClaim[];
}

function formatMoney(amount: number, currency: string | null): string {
  if (!currency) return amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
}

const RELEASE_STATE_BADGE: Record<string, { label: string; cls: string }> = {
  breeder_only:    { label: 'Breeder only',    cls: 'bg-neutral-100 text-neutral-700' },
  region_licensor: { label: 'Region licensors', cls: 'bg-amber-100 text-amber-800' },
  public:          { label: 'Public',          cls: 'bg-success-100 text-success-800' },
};

function BreederDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: ApiResponse<DashboardData> = await apiService.get('/breeder/dashboard');
      if (res.success && res.data) setData(res.data);
      else setError(res.message || 'Failed to load dashboard');
    } catch (err) {
      const msg = (err as { message?: string } | null)?.message
        || 'Failed to load dashboard';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleApprove = async (id: number) => {
    setActioning(id);
    try {
      await breederService.approveGrant(id);
      await load();
    } finally { setActioning(null); }
  };

  const handleReject = async (id: number) => {
    setActioning(id);
    try {
      await breederService.rejectGrant(id);
      await load();
    } finally { setActioning(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Breeder Dashboard"
        subtitle="Your owned cultivars, sub-licences, and planting reach"
        icon={<Award className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4 flex justify-end">
          <Link to="/breeder/licensors">
            <Button variant="outline" size="sm"><Settings className="h-4 w-4 mr-1" /> Manage region licensors</Button>
          </Link>
        </div>

        <div className="mb-6"><OwedToUsCard /></div>

        {loading ? (
          <Card><CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-3" />
            <p className="text-neutral-600">Loading dashboard…</p>
          </CardContent></Card>
        ) : error ? (
          <Card><CardContent className="p-12 text-center">
            <AlertCircle className="h-10 w-10 text-error-600 mx-auto mb-3" />
            <p className="text-error-700">{error}</p>
          </CardContent></Card>
        ) : data ? (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
              <StatCard icon={<Leaf className="h-5 w-5 text-primary-600" />} label="Cultivars" value={data.totals.cultivars} />
              <StatCard icon={<Users className="h-5 w-5 text-primary-600" />} label="Active sub-licences" value={data.totals.sublicenses_active} />
              <StatCard icon={<TreePine className="h-5 w-5 text-primary-600" />} label="Plantings" value={data.totals.plantings} />
              <StatCard icon={<TreePine className="h-5 w-5 text-primary-600" />} label="Trees" value={data.totals.trees.toLocaleString()} />
              <StatCard icon={<Globe2 className="h-5 w-5 text-primary-600" />} label="Hectares" value={Number(data.totals.hectares).toFixed(2)} />
              <StatCard
                icon={<DollarSign className="h-5 w-5 text-amber-600" />}
                label="Annual royalty (actual)"
                value={formatMoney(Number(data.totals.annual_royalty_actual), data.royalty_currency)}
                sublabel={
                  data.totals.per_tree_royalty > 0 || data.totals.actual_production_royalty > 0
                    ? `${formatMoney(data.totals.per_tree_royalty, data.royalty_currency)} trees · ${formatMoney(data.totals.actual_production_royalty, data.royalty_currency)} declared`
                    : 'No declarations yet'
                }
              />
              <StatCard
                icon={<DollarSign className="h-5 w-5 text-indigo-600" />}
                label="Annual royalty (default)"
                value={formatMoney(Number(data.totals.annual_royalty_default), data.royalty_currency)}
                sublabel={
                  data.totals.default_production_royalty > 0
                    ? `${formatMoney(data.totals.per_tree_royalty, data.royalty_currency)} trees · ${formatMoney(data.totals.default_production_royalty, data.royalty_currency)} at default yield`
                    : 'No program defaults set'
                }
              />
            </div>

            {data.royalty_currency_mixed && (
              <div className="mb-6 bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800">
                <AlertCircle className="inline h-4 w-4 mr-1" />
                Programs on your cultivars use multiple currencies — the total above is a naive sum and may not be meaningful. Per-cultivar amounts in the table below are correct in their own currency.
              </div>
            )}

            {/* Pending licensor claims */}
            {data.pending_claims.length > 0 && (
              <Card className="mb-6">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-neutral-900 mb-2">Pending licensor claims</h2>
                  <p className="text-sm text-neutral-600 mb-4">
                    Orgs requesting to act as your region licensor. Approve to give them visibility of your cultivars in their region.
                  </p>
                  <ul className="divide-y divide-neutral-200">
                    {data.pending_claims.map(c => (
                      <li key={c.id} className="py-3 flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium text-neutral-900">{c.licensor_org_name}</div>
                          <div className="text-sm text-neutral-600">
                            Region <span className="font-mono">{c.region_code}</span> · {c.scope.replace('_', ' ')}
                          </div>
                          {c.notes && <div className="text-sm text-neutral-500 italic mt-1">"{c.notes}"</div>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => handleReject(c.id)} disabled={actioning === c.id}>
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                          <Button variant="primary" size="sm" onClick={() => handleApprove(c.id)} disabled={actioning === c.id}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Owned cultivars */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-neutral-900 mb-4">Owned cultivars</h2>
                {data.cultivars.length === 0 ? (
                  <p className="text-sm text-neutral-500">
                    No cultivars are attributed to your organisation as breeder yet. Open a cultivar in the registry and set your org as the breeder, or ask a platform admin to attribute existing cultivars to you.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-neutral-500 border-b border-neutral-200">
                          <th className="py-2 pr-3 font-medium">Cultivar</th>
                          <th className="py-2 pr-3 font-medium">Crop</th>
                          <th className="py-2 pr-3 font-medium">Release</th>
                          <th className="py-2 pr-3 font-medium text-right">Active sub-lic.</th>
                          <th className="py-2 pr-3 font-medium text-right">Plantings</th>
                          <th className="py-2 pr-3 font-medium text-right">Trees</th>
                          <th className="py-2 pr-3 font-medium text-right">Hectares</th>
                          <th className="py-2 pr-3 font-medium text-right">Regions w/ licensor</th>
                          <th className="py-2 pr-3 font-medium text-right">Royalty (actual)</th>
                          <th className="py-2 pr-3 font-medium text-right">Royalty (default)</th>
                          <th className="py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.cultivars.map(c => {
                          const badge = c.release_state ? RELEASE_STATE_BADGE[c.release_state] : null;
                          return (
                            <tr key={c.id} className="border-b border-neutral-100">
                              <td className="py-2 pr-3">
                                <Link to={`/cultivar/${c.id}`} className="text-primary-700 hover:underline font-medium">
                                  {c.primary_display_synonym || c.name}
                                </Link>
                                {c.primary_display_synonym && (
                                  <div className="text-xs text-neutral-500">(displayed; registered as {c.name})</div>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-neutral-700">{c.crop_type_name || '—'}</td>
                              <td className="py-2 pr-3">
                                {c.legacy_visibility_grace ? (
                                  <span className="px-2 py-0.5 rounded text-xs bg-warning-100 text-warning-800">Needs review</span>
                                ) : badge ? (
                                  <span className={`px-2 py-0.5 rounded text-xs ${badge.cls}`}>{badge.label}</span>
                                ) : <span className="text-neutral-400">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">{c.sublicenses_active}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{c.plantings_count}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{Number(c.tree_count_total).toLocaleString()}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{Number(c.hectares_total).toFixed(2)}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{c.regions_with_licensor}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {Number(c.annual_royalty_actual) > 0 ? (
                                  <span
                                    title={[
                                      `Trees: ${formatMoney(Number(c.per_tree_royalty), c.royalty_currency)}`,
                                      `Declared production: ${formatMoney(Number(c.actual_production_royalty), c.royalty_currency)}${c.latest_production_season ? ` (season ${c.latest_production_season})` : ''}`,
                                    ].join('\n')}
                                    className="cursor-help"
                                  >
                                    {formatMoney(Number(c.annual_royalty_actual), c.royalty_currency)}
                                  </span>
                                ) : <span className="text-neutral-400">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {Number(c.annual_royalty_default) > 0 ? (
                                  <span
                                    title={[
                                      `Trees: ${formatMoney(Number(c.per_tree_royalty), c.royalty_currency)}`,
                                      `Default production: ${formatMoney(Number(c.default_production_royalty), c.royalty_currency)} (program default yield × plantings)`,
                                    ].join('\n')}
                                    className="cursor-help text-indigo-700"
                                  >
                                    {formatMoney(Number(c.annual_royalty_default), c.royalty_currency)}
                                  </span>
                                ) : <span className="text-neutral-400">—</span>}
                              </td>
                              <td className="py-2 text-right">
                                <div className="flex justify-end gap-2">
                                  <Link to={`/breeder/cultivars/${c.id}/map`}>
                                    <Button variant="outline" size="sm">Map</Button>
                                  </Link>
                                  <Link to={`/cultivar/${c.id}/edit`}>
                                    <Button variant="outline" size="sm">Manage</Button>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}

function StatCard({
  icon, label, value, sublabel,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sublabel?: React.ReactNode;
}) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-neutral-500 uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-semibold text-neutral-900 tabular-nums">{value}</div>
          {sublabel && <div className="text-xs text-neutral-500 mt-1">{sublabel}</div>}
        </div>
        {icon}
      </div>
    </CardContent></Card>
  );
}

export default BreederDashboard;
